import { action, computed, makeObservable, observable } from 'mobx';
import { AdT } from 'src/ads/types';
import { ExplanationT } from 'src/api/types/ExplanationT';
import { QuestionT } from 'src/api/types/QuestionT';
import { questionMustBeSkipped } from 'src/questions/utils/questionMustBeSkipped';
import { AnswersState } from 'src/selfTest/SelfTestState/AnswersState';
import { getUrlParam } from 'src/utils/getUrlParam';
import { trackEvent } from '../../tracking/utils/initTracking';
import { GaProvider } from '../../tracking/utils/providers/ga-provider';
import {updateBCNHelper} from "../../ads/utils/initAds";

// Note that we also show an ad just before the summary page. This ad is not included
// in the adPageIdxs array. If the showAds flag is false then this array is ignored.
const adPageIdxs = [3, 7];

export class PagesState {
  showAds = getUrlParam('showAds') === '1' && !!getUrlParam('csid');
  enableTracking = getUrlParam('enableTracking') === '1';

  @observable answersState: AnswersState;
  // If the pageIdx is 0 then we are on the landing page
  @observable pageIdx: number = 0;
  // This is the (consecutive) question number that we show to the user
  @observable questionDisplayNr: number = 0;
  @observable questions: QuestionT[] = [];

  constructor(questions: QuestionT[], answersState: AnswersState) {
    this.questions = questions;
    this.answersState = answersState;
    makeObservable(this);
  }

  @computed get nrOfAds() {
    if (!this.showAds) {
      return 0;
    }

    // After showing the last ad, we want to show at least one more question, and then
    // show the pre-summary ad. Therefore, we only want to show the n-th add if the number
    // of questions exceeds the number of questions we've asked before showing the n-th ad.
    let nrOfInBetweenAds = 0;
    for (let n = adPageIdxs.length; n >= 1; --n) {
      const nrOfQuestionsAskedBeforeShowingNthAd = adPageIdxs[n - 1] - n;
      if (this.questions.length > nrOfQuestionsAskedBeforeShowingNthAd) {
        nrOfInBetweenAds = n;
        break;
      }
    }

    return nrOfInBetweenAds + 1; // We add one to account for the pre-summary page ad.
  }

  @action
  goToNextPage = () => {
    // Needs to be called before the actual page change
    updateBCNHelper();

    if (this.pageIdx >= this.summaryPageIdx) {
      this.pageIdx = this.summaryPageIdx; // Stay on the last page
    } else {
      this.pageIdx++;
      if (
        this.question &&
        questionMustBeSkipped(this.question, this.answersState.givenAnswers)
      ) {
        this.goToNextPage();
      } else if (this.question) {
        this.questionDisplayNr++;
      }
    }

    if (this.pageType) {
      trackEvent('page_loaded', this.pageType, this.pageLabel);
    }
  };

  @action
  goToSummaryPage = () => {
    // Needs to be called before the actual page change
    updateBCNHelper();

    this.pageIdx = this.summaryPageIdx;
  };

  @action
  rollBackToPreviousPage = () => {
    if (!this.ad) {
      // Needs to be called before the actual page change
      updateBCNHelper();
      trackEvent('click_back', this.pageType || 'General', this.pageLabel);
    }

    if (this.pageIdx <= 0) {
      this.pageIdx = 0; // Stay on the first page
    } else {
      if (
        this.question &&
        !questionMustBeSkipped(this.question, this.answersState.givenAnswers)
      ) {
        this.answersState.deleteAnswer(this.question);
        this.questionDisplayNr--;
      }

      this.pageIdx--;

      if (this.pageType && !this.ad) {
        trackEvent('page_loaded', this.pageType, this.pageLabel);
      }

      if (
        this.ad ||
        (this.question &&
          questionMustBeSkipped(this.question, this.answersState.givenAnswers))
      ) {
        this.rollBackToPreviousPage();
      }
    }
  };

  @action
  goToExplanationPage = (explanationPageIdx: number) => {
    // Needs to be called before the actual page change
    updateBCNHelper();

    this.pageIdx = this.summaryPageIdx + 1 + explanationPageIdx;

    if (this.pageType) {
      trackEvent('page_loaded', this.pageType, this.pageLabel);
    }
  };

  @computed
  get questionIdx() {
    let correction = 1; // correct for the landing page

    if (this.showAds) {
      for (const adPageIdx of adPageIdxs) {
        if (this.pageIdx >= adPageIdx) {
          correction++;
        }
      }
    }

    return this.pageIdx - correction;
  }

  @computed
  get question() {
    return this.pageIdx < 1 || this.pageIdx >= this.summaryPageIdx || this.ad
      ? undefined
      : this.questions[this.questionIdx];
  }

  @computed
  get ad(): AdT | undefined {
    const preSummaryAdPageIdx = this.summaryPageIdx - 1;
    const adPosition =
      this.pageIdx === preSummaryAdPageIdx
        ? adPageIdxs.length
        : adPageIdxs.indexOf(this.pageIdx);

    return !this.showAds
      ? undefined
      : this.pageIdx >= this.summaryPageIdx
      ? undefined
      : this.pageIdx === preSummaryAdPageIdx - 1
      ? undefined // Do not show an ad just before the pre-summary ad-page
      : adPosition >= 0
      ? { position: adPosition + 1 }
      : undefined;
  }

  @computed
  get explanationPageIdx(): number | undefined {
    const explanationPageIdx = this.pageIdx - this.summaryPageIdx - 1;
    return explanationPageIdx < 0 ? undefined : explanationPageIdx;
  }

  @computed
  get explanation(): ExplanationT | undefined {
    return this.explanationPageIdx === undefined
      ? undefined
      : this.answersState.getExplanation(this.explanationPageIdx);
  }

  @computed
  get summaryPageIdx() {
    return 1 + this.questions.length + this.nrOfAds;
  }

  @computed
  get pageType(): GaProvider.EventCategory {
    if (this.pageIdx === 0) {
      return 'Start Page';
    }

    if (this.question) {
      return 'Question Page';
    }

    if (this.ad) {
      return 'Ad Page';
    }

    if (this.pageIdx === this.summaryPageIdx) {
      return 'Result Page';
    }

    if (this.explanation) {
      return this.explanation.category === 'RISKFACTOR'
        ? 'Riskfactor Page'
        : 'Tip Page';
    }

    return '';
  }

  @computed
  get pageLabel() {
    if (this.question) {
      return `${this.questionDisplayNr}_${this.question?.text}`;
    }

    if (this.ad) {
      return `${this.ad.position}`;
    }

    if (this.pageIdx === this.summaryPageIdx) {
      return this.resultLabel;
    }

    if (this.explanation) {
      const explanationPageIdx = this.explanationPageIdx || 0;
      const index =
        this.explanation.category === 'RISKFACTOR'
          ? explanationPageIdx + 1
          : explanationPageIdx - this.answersState.riskfactors.length + 1;

      return `${index}_${this.explanation?.title}`;
    }

    return '';
  }

  @computed
  get resultLabel() {
    const scoreRange = this.answersState.getScoreRange(
      this.answersState.totalScore
    );

    return `${scoreRange?.name}_${scoreRange?.id}`;
  }
}
